import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { type Document } from '@/features/documents/Document'
import { type DocumentStore } from '@/features/documents/DocumentStore'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { PromptDeleteDocumentOptions } from '@/features/documents'

/**
 * View model for confirming the permanent deletion of a document.
 */
export class DeleteDocumentDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  @observable accessor document: Document | null = null

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _documentStore: DocumentStore,
  ) {}

  /**
   * Asks the user to confirm, then deletes the document.
   *
   * @returns Whether the document was deleted.
   */
  async prompt({ document, signal }: PromptDeleteDocumentOptions) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.document = document
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()

    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  delete = task.resolved(async () => {
    const document = this.document
    if (!document) {
      return
    }

    const deleted = await this._documentStore
      .archive(document)
      .then(() => true)
      .catch(this._createAlertErrorHandler('Deleting failed'))
      .catch(() => false)
    if (!deleted) {
      return
    }

    this._newAlert()
      .withState('success')
      .saying(`Success!  Document ${document.title} has been removed!`)
      .autoDismiss()
      .show()

    this._prompt.respond(true)
    this.modal.close()
  })
}
